
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { UserMenu } from "@/components/UserMenu";
import Footer from "@/components/Footer";

const Privacy = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="w-full border-b border-border sticky top-0 left-0 right-0 z-50 bg-background">
        <div className="container mx-auto py-4 px-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img
              src="/lovable-uploads/82002023-ce31-46c5-b6ce-14f9cdbffeaa.png"
              alt="Convergence Logo"
              className="w-8 h-8"
            />
            <Link to="/" className="text-xl font-semibold">
              Convergence
            </Link>
          </div>
          <UserMenu />
        </div>
      </header>

      <main className="container mx-auto py-8 px-4 flex-1">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink>Privacy Policy</BreadcrumbLink>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <Card>
          <CardHeader>
            <CardTitle className="text-3xl">Privacy Policy</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm md:prose-base max-w-none text-left space-y-6">
            <p>Last updated: {new Date().toLocaleDateString()}</p>
            
            <h2 className="text-xl font-semibold">1. Introduction</h2>
            <p>
              This Privacy Policy explains how Convergence collects, uses, and protects your information when you play our word guessing game. We respect your privacy and are committed to protecting your personal information.
            </p>
            
            <h2 className="text-xl font-semibold">2. Information We Collect</h2>
            <h3 className="text-lg font-medium">2.1 Account Information</h3>
            <p>
              When you create an account, we may collect:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Email address</li>
              <li>Name (if provided through Google authentication)</li>
              <li>Profile picture (if provided through Google authentication)</li>
            </ul>
            
            <h3 className="text-lg font-medium">2.2 Gameplay Information</h3>
            <p>
              We collect data related to your gameplay, including:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Words you submit during the game</li>
              <li>Game scores and results</li>
              <li>Word proximity scores</li>
              <li>Game completion statistics</li>
            </ul>
            
            <h3 className="text-lg font-medium">2.3 Technical Information</h3>
            <p>
              We automatically collect certain technical information when you use our game, such as:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Device type</li>
              <li>Operating system</li>
              <li>Game session data</li>
            </ul>
            
            <h2 className="text-xl font-semibold">3. How We Use Your Information</h2>
            <p>
              We use the collected information for the following purposes:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>To provide and maintain the game functionality</li>
              <li>To create and manage your account</li>
              <li>To save your game progress and statistics</li>
              <li>To generate appropriate AI word selections</li>
              <li>To improve the game experience</li>
              <li>To analyze gameplay patterns and optimize game mechanics</li>
              <li>To communicate with you about game updates or changes</li>
            </ul>
            
            <h2 className="text-xl font-semibold">4. Word Selection and AI Processing</h2>
            <p>
              Convergence uses artificial intelligence to select words and calculate proximity scores. The words you submit are processed to determine how closely they match the AI's selection. This processing is essential to the game mechanics.
            </p>
            
            <h2 className="text-xl font-semibold">5. Data Storage and Security</h2>
            <p>
              Your information is stored securely in our Supabase database. We implement appropriate security measures to protect against unauthorized access, alteration, disclosure, or destruction of your personal information.
            </p>
            
            <h2 className="text-xl font-semibold">6. Third-Party Services</h2>
            <p>
              We use the following third-party services:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Supabase:</strong> For database and authentication services</li>
              <li><strong>Google OAuth:</strong> For authentication if you choose to sign in with Google</li>
            </ul>
            <p>
              These services have their own privacy policies that govern how they handle your information.
            </p>
            
            <h2 className="text-xl font-semibold">7. Data Retention</h2>
            <p>
              We retain your game history and account information for as long as your account is active. If you delete your account, we will delete or anonymize your personal information within 30 days.
            </p>
            
            <h2 className="text-xl font-semibold">8. Your Rights</h2>
            <p>
              Depending on your location, you may have rights regarding your personal information, including:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Accessing your personal information</li>
              <li>Correcting inaccurate information</li>
              <li>Deleting your information</li>
              <li>Restricting or objecting to processing</li>
              <li>Data portability</li>
            </ul>
            <p>
              To exercise these rights, please contact us using the information provided below.
            </p>
            
            <h2 className="text-xl font-semibold">9. Updates to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. When we do, we will notify you by revising the "Last Updated" date at the top of this page. We encourage you to review this Privacy Policy periodically.
            </p>
            
            <h2 className="text-xl font-semibold">10. Contact Us</h2>
            <p>
              If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at privacy@convergence-game.com.
            </p>
          </CardContent>
        </Card>
      </main>
      
      <Footer />
    </div>
  );
};

export default Privacy;
